import React, { memo, useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Add, Close, DragIndicator } from '@mui/icons-material';
import type { PaginationState } from '@tanstack/react-table';
import { Paginator } from 'components/common/Table/paginator';
import { useLogin } from 'contexts/index';
import { Chip, LinearProgress, Typography } from '@mui/material';
import { Content, Header, Row, SearchControls, Subtitle, Tags } from './List.styles';
import { Button } from 'components/mui/common/Button';
import { Switch } from 'components/mui/common/Switch';
import { IconButton } from 'components/mui/common/IconButton';
import { SearchField } from './Table/actions';
import { ActionModal } from 'components/mui/common/ActionModal';
import { useBooleanState } from 'utils/hooks';
import { arrayMove, SortableContext, sortableKeyboardCoordinates, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import type { DragEndEvent } from '@dnd-kit/core';
import { closestCenter, DndContext, KeyboardSensor, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';

const ListRow = ({
  item,
  onClick,
  onRemove,
  onToggleState,
  isReorderable,
}: {
  item: DataType;
  onClick: () => void;
  onRemove: (id: string) => void;
  onToggleState?: (id: string, newState: string) => void;
  isReorderable: boolean;
}) => {
  const isToggleable = Boolean(onToggleState);
  const isActive = item.state === 'Active';
  const { t } = useTranslation();
  const stateLabel = item.state ? t(`common.stateLabels.${item.state.toLowerCase()}`, { defaultValue: item.state }) : '';

  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: item.id,
    disabled: !isReorderable,
  });

  const style = {
    transform: CSS.Translate.toString(transform),
    transition,
    zIndex: isDragging ? 50 : 'auto',
    opacity: isDragging ? 0.5 : 1,
    position: 'relative' as const,
  };

  return (
    <Row ref={setNodeRef} style={style} onClick={onClick}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
        {isReorderable && (
          <IconButton {...attributes} {...listeners} sx={{ cursor: 'grab', touchAction: 'none' }}>
            <DragIndicator />
          </IconButton>
        )}
        {isToggleable && (
          <Switch
            checked={isActive}
            title={stateLabel}
            onClick={(e) => e.stopPropagation()}
            onChange={(e) => {
              e.stopPropagation();
              onToggleState?.(item.id, e.target.checked ? 'ACTIVE' : 'DISABLED');
            }}
          />
        )}
        <div>
          <Typography>{item.name}</Typography>
          {item.description && <Subtitle variant="subtitle2">{item.description}</Subtitle>}
          {!!item.tags?.length && (
            <Tags>
              {item.tags.map((tag) => (
                <Chip key={tag} label={tag} size="small" variant="outlined" />
              ))}
            </Tags>
          )}
        </div>
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
        <IconButton
          onClick={(e) => {
            e.stopPropagation();
            onRemove(item.id);
          }}>
          <Close />
        </IconButton>
      </div>
    </Row>
  );
};

type Props = {
  label?: string;
  data?: DataType[];
  defaultPageIndex?: number;
  onButtonClick: () => void;
  onDetailClick: (id: string, name?: string) => void;
  onRemoveClick: (id: string) => boolean | void | Promise<boolean | void>;
  onReorder?: (newData: DataType[]) => void;
  onToggleState?: (id: string, newState: string) => void;
  onPageChange?: (pageIndex: number) => void;
  searchValue?: string;
  onSearchChange?: (search: string) => void;
  pagination?: PaginationState;
  onPaginationChange?: (pagination: PaginationState) => void;
  totalCount?: number;
  isFetching?: boolean;
  toolbarActions?: React.ReactNode;
  removeModalTitle?: string;
  removeModalText?: string;
};
export interface DataType {
  id: string;
  name: string;
  description?: string | null;
  order?: number;
  type?: string;
  state?: string;
  tags?: string[];
}

export const List = memo(
  ({
    label,
    data = [],
    defaultPageIndex = 0,
    onButtonClick,
    onDetailClick,
    onRemoveClick,
    onReorder,
    onToggleState,
    onPageChange,
    searchValue,
    onSearchChange,
    pagination,
    onPaginationChange,
    totalCount,
    isFetching = false,
    toolbarActions,
    removeModalTitle,
    removeModalText,
  }: Props) => {
    const { t } = useTranslation();
    const [localSearch, setLocalSearch] = useState('');
    const [sectionId, setSectionId] = useState('');
    const [removeModal, toggleRemoveModal] = useBooleanState(false);
    const [localPaginator, setLocalPaginator] = useState<PaginationState>({
      pageIndex: defaultPageIndex,
      pageSize: 10,
    });
    const isServerControlled = pagination !== undefined && onPaginationChange !== undefined;
    const paginator = pagination ?? localPaginator;
    const search = onSearchChange ? (searchValue ?? '') : localSearch;
    const templatesData = useMemo(
      () => (onSearchChange ? data : data.filter((template) => template?.name?.toLowerCase().includes(search.toLowerCase()))),
      [data, onSearchChange, search]
    );
    const rowsCount = isServerControlled ? (totalCount ?? data.length) : templatesData.length;

    const setPaginator = useCallback<React.Dispatch<React.SetStateAction<PaginationState>>>(
      (value) => {
        const update = (previous: PaginationState) => {
          const next = typeof value === 'function' ? value(previous) : value;
          return next.pageSize === previous.pageSize ? next : { ...next, pageIndex: 0 };
        };

        if (isServerControlled) {
          onPaginationChange(update(paginator));
          return;
        }

        setLocalPaginator(update);
      },
      [isServerControlled, onPaginationChange, paginator]
    );

    useEffect(() => {
      if (isServerControlled) return;

      setLocalPaginator((prev) => {
        const maxPageIndex = Math.max(0, Math.ceil(rowsCount / prev.pageSize) - 1);
        const clampedDefaultPageIndex = Math.min(Math.max(0, defaultPageIndex), maxPageIndex);
        if (prev.pageIndex === clampedDefaultPageIndex) return prev;
        return { ...prev, pageIndex: clampedDefaultPageIndex };
      });
    }, [defaultPageIndex, isServerControlled, rowsCount]);

    useEffect(() => {
      if (!isServerControlled || totalCount === undefined) return;

      const maxPageIndex = Math.max(0, Math.ceil(totalCount / paginator.pageSize) - 1);
      if (paginator.pageIndex <= maxPageIndex) return;
      onPaginationChange?.({ ...paginator, pageIndex: maxPageIndex });
    }, [isServerControlled, onPaginationChange, paginator, totalCount]);

    const { permissions } = useLogin();
    const isTemplateAdmin = permissions?.templateAdmin;

    const start = paginator.pageIndex * paginator.pageSize;
    const end = start + paginator.pageSize;

    const paginatedData = isServerControlled ? templatesData : templatesData.slice(start, end);

    const onDetailClickHandler = useCallback(
      (item: DataType) => () => {
        onDetailClick(item.id, item.name);
      },
      [onDetailClick]
    );

    const handleRemove = useCallback(async () => {
      if (!sectionId) return;
      const result = await onRemoveClick(sectionId);
      if (result) {
        setSectionId('');
        toggleRemoveModal();
      }
    }, [onRemoveClick, sectionId, toggleRemoveModal]);

    const handleSearch = useCallback(
      (e: React.ChangeEvent<HTMLInputElement>) => {
        const nextSearch = e.target.value;
        if (nextSearch === search) {
          return;
        }

        setPaginator((prev) => ({
          ...prev,
          pageIndex: 0,
        }));
        if (paginator.pageIndex !== 0) {
          onPageChange?.(0);
        }
        if (onSearchChange) {
          onSearchChange(nextSearch);
        } else {
          setLocalSearch(nextSearch);
        }
      },
      [onPageChange, onSearchChange, paginator.pageIndex, search, setPaginator]
    );

    const sensors = useSensors(
      useSensor(PointerSensor),
      useSensor(KeyboardSensor, {
        coordinateGetter: sortableKeyboardCoordinates,
      })
    );

    const handleDragEnd = (event: DragEndEvent) => {
      const { active, over } = event;

      if (over && active.id !== over.id && onReorder) {
        const oldIndex = data.findIndex((item) => item.id === active.id);
        const newIndex = data.findIndex((item) => item.id === over.id);

        onReorder(arrayMove(data, oldIndex, newIndex));
      }
    };

    return (
      <div>
        <Header>
          <Typography variant="h3">{label}</Typography>
          <Button variant="outlined" startIcon={<Add />} onClick={onButtonClick} disabled={!isTemplateAdmin}>
            {t('common.add')}
          </Button>
        </Header>
        <Content>
          <SearchControls>
            <SearchField placeholder={t('common.search._')} value={search} onChange={handleSearch} maxWidth="100%" />
            {toolbarActions}
          </SearchControls>
          {isFetching && <LinearProgress />}
          <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
            <SortableContext items={paginatedData.map((d) => d.id)} strategy={verticalListSortingStrategy}>
              {paginatedData.map((item) => (
                <ListRow
                  key={item.id + item.type}
                  item={item}
                  isReorderable={Boolean(onReorder)}
                  onClick={onDetailClickHandler(item)}
                  onToggleState={onToggleState}
                  onRemove={(id) => {
                    setSectionId(id);
                    toggleRemoveModal();
                  }}
                />
              ))}
            </SortableContext>
          </DndContext>
          <Paginator
            rowsCount={rowsCount}
            paginator={paginator}
            setPaginator={setPaginator}
            onPageIndexChange={(nextPageIndex) => {
              if (nextPageIndex !== paginator.pageIndex) {
                onPageChange?.(nextPageIndex);
              }
            }}
          />
        </Content>
        <ActionModal
          maxWidth="xs"
          title={removeModalTitle ?? t('flow.agendasAndModules.conditions.confirmationDeletionModal.title')}
          open={removeModal}
          onClose={toggleRemoveModal}
          onCancel={toggleRemoveModal}
          onSubmit={handleRemove}>
          <Typography>{removeModalText ?? t('flow.agendasAndModules.conditions.confirmationDeletionModal.description')}</Typography>
        </ActionModal>
      </div>
    );
  }
);
